import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import './Caja.css';

const CajaAbonos = () => {
  const { user } = useContext(AuthContext);
  const [balance, setBalance] = useState(null);
  const [movements, setMovements] = useState([]);
  const [showAbonoWithdrawalModal, setShowAbonoWithdrawalModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [abonoWithdrawalForm, setAbonoWithdrawalForm] = useState({ description: '', amount: '', payment_source: '', created_at: new Date().toISOString().split('T')[0] });
  const [expenseForm, setExpenseForm] = useState({ description: '', amount: '', payment_source: '', created_at: new Date().toISOString().split('T')[0] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchBalance = useCallback(async () => {
    try {
      const response = await axios.get(`/api/cash/balance?startDate=${startDate}&endDate=${endDate}`);
      const data = response.data.balance !== undefined && typeof response.data.balance === 'object' 
        ? response.data.balance 
        : response.data;

      setBalance({
        balance_abonos: parseFloat(data.balance_abonos) || 0,
        total_pendiente_global: parseFloat(data.total_pendiente_global) || 0,
        portfolio_by_month: data.portfolio_by_month || [],
        cartera_pendiente: parseFloat(data.cartera_pendiente) || 0,
        cartera_pagada: parseFloat(data.cartera_pagada) || 0,
        cupl_count: parseInt(data.cupl_count) || 0
      });
    } catch (error) {
      console.error('Error al obtener saldo:', error);
      setError('Error al obtener saldo de abonos');
    }
  }, [startDate, endDate]);

  const [totalRetiros, setTotalRetiros] = useState(0);
  const [totalGastos, setTotalGastos] = useState(0);
  
  const fetchMovements = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/cash/movements?startDate=${startDate}&endDate=${endDate}`);
      // Solo nos interesan los tipos relacionados con abonos
      const abonoMovements = response.data.filter(m => ['abono_cartera', 'egreso_cartera', 'gasto_cartera'].includes(m.type));
      setMovements(abonoMovements);
      
      // Calculate separate totals
      const totalRetiroValue = abonoMovements
        .filter(m => m.type === 'egreso_cartera')
        .reduce((sum, m) => sum + parseFloat(m.amount), 0);
      const totalGastoValue = abonoMovements
        .filter(m => m.type === 'gasto_cartera')
        .reduce((sum, m) => sum + parseFloat(m.amount), 0);
      setTotalRetiros(totalRetiroValue);
      setTotalGastos(totalGastoValue);
      
      setError(null);
    } catch (error) {
      console.error('Error al obtener movimientos:', error);
      setError('Error al obtener historial de abonos');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchBalance();
    fetchMovements();
  }, [fetchBalance, fetchMovements]);

  const handlePayPortfolio = async (e) => {
    e.preventDefault();
    if (!paymentAmount || paymentAmount <= 0) {
      setError('Por favor ingrese un monto válido');
      return;
    }
    try {
      await axios.post(`/api/cash/portfolio/pay-global`, {
        amount: parseFloat(paymentAmount),
        paymentDate: paymentDate
      });
      setShowPaymentModal(false);
      setPaymentAmount('');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      fetchBalance();
      fetchMovements();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al procesar pago');
    }
  };

  const handleAddAbonoWithdrawal = async (e) => {
    e.preventDefault();
    try {
       await axios.post('/api/cash/movement', { ...abonoWithdrawalForm, type: 'egreso_cartera' });
       setShowAbonoWithdrawalModal(false);
       setAbonoWithdrawalForm({ description: '', amount: '', payment_source: '', created_at: new Date().toISOString().split('T')[0] });
       fetchBalance();
       fetchMovements();
    } catch (error) {
       setError('Error al registrar retiro');
    }
  };

  const handleSaveExpense = async (e) => {
    e.preventDefault();
    if (!expenseForm.description || !expenseForm.amount) {
      setError('Por favor complete los campos obligatorios');
      return;
    }
    
    try {
      const payload = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
        type: 'gasto_cartera'  // New type for gastos
      };
      
      await axios.post('/api/cash/movement', payload);
      
      setShowExpenseModal(false);
      setExpenseForm({ description: '', amount: '', payment_source: '', created_at: new Date().toISOString().split('T')[0] });
      fetchBalance();
      fetchMovements();
      setError(null);
    } catch (err) {
      console.error('Error al guardar gasto:', err);
      setError('Error al guardar el gasto');
    }
  };

  const formatCurrency = (value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '$ 0';
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(numValue);
  };

  return (
    <div className="app-layout">
      <Sidebar userRole={user?.role} />
      <div className="main-content">
        <Navbar title="💰 Caja de Abonos" />

        <div className="page-content">
          {error && <div className="error-message">{error}</div>}

          <div className="date-filters" style={{ display: 'flex', gap: '20px', marginBottom: '25px', background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', alignItems: 'flex-end' }}>
            <div className="filter-group">
              <label>Fecha Desde</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="filter-group">
              <label>Fecha Hasta</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>

          {balance && (
            <>
              <div className="balance-summary" style={{ marginBottom: '30px' }}>
                <div className="balance-card portfolio" style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)' }}>
                  <h3>Saldo Disponible en Abonos</h3>
                  <p className="balance-amount">{formatCurrency(balance.balance_abonos)}</p>
                </div>
                <div className="balance-card portfolio-pending" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)' }}>
                  <h3>Deuda Total Global</h3>
                  <p className="balance-amount">{formatCurrency(balance.total_pendiente_global)}</p>
                </div>
                <div className="balance-card" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                  <h3>Total Retiros</h3>
                  <p className="balance-amount">{formatCurrency(totalRetiros)}</p>
                </div>
                <div className="balance-card" style={{ background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)' }}>
                  <h3>Total Gastos</h3>
                  <p className="balance-amount">{formatCurrency(totalGastos)}</p>
                </div>
              </div>

              <div style={{ background: '#fff1f2', padding: '20px', borderRadius: '12px', marginBottom: '40px', border: '1px solid #fecaca' }}>
                <h3 style={{ margin: '0 0 15px 0', color: '#b91c1c', fontSize: '16px' }}>🚩 Detalle de Deuda por Mes</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                  {balance.portfolio_by_month?.map((item, idx) => (
                    <div key={idx} style={{ background: 'white', padding: '12px', borderRadius: '8px', borderLeft: item.pendiente > 0 ? '4px solid #ef4444' : '4px solid #10b981' }}>
                      <span style={{ fontSize: '12px', color: '#64748b' }}>{item.month_name}</span>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px' }}>
                        <strong style={{ color: item.pendiente > 0 ? '#b91c1c' : '#10b981' }}>{formatCurrency(item.pendiente)}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="caja-actions" style={{ display: 'flex', gap: '15px', marginBottom: '25px' }}>
            <button className="btn-profit" onClick={() => setShowPaymentModal(true)} style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)', color: 'white' }}>
              📥 Agregar Pago Cartera
            </button>
            <button className="btn-warning" onClick={() => setShowAbonoWithdrawalModal(true)} style={{ background: '#f59e0b', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold' }}>
              💸 Retirar de Abonos
            </button>
            <button onClick={() => setShowExpenseModal(true)} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
              💵 Registrar Gasto
            </button>
          </div>

          <div className="movements-table-container" style={{ borderTop: '4px solid #7c3aed' }}>
            <h2>💰 Historial de Caja Abonos</h2>
            {loading ? <p>Cargando...</p> : (
              <table className="movements-table">
                <thead>
                  <tr><th>Fecha</th><th>Descripción</th><th>Beneficiario/Origen</th><th>Monto</th></tr>
                </thead>
                <tbody>
                  {movements.map((m) => (
                    <tr key={m.id} style={{ 
                      background: m.type === 'abono_cartera' ? '#fdfaff' : 
                                 m.type === 'egreso_cartera' ? '#fffbeb' : '#ffe4e6' 
                    }}>
                      <td>{new Date(m.created_at).toLocaleDateString('es-CO')}</td>
                      <td>
                        <small style={{ display: 'block', fontSize: '10px' }}>
                          {m.type === 'abono_cartera' ? '📥 INGRESO' : 
                           m.type === 'egreso_cartera' ? '📤 RETIRO' : '💸 GASTO'}
                        </small>
                        {m.description}
                      </td>
                      <td>{m.payment_source || '-'}</td>
                      <td className={m.type === 'abono_cartera' ? 'amount-income' : 'amount-expense'}>
                        {m.type === 'abono_cartera' ? '+' : '-'} {formatCurrency(m.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Modales */}
        {showPaymentModal && (
          <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Registrar Pago de Cartera</h2>
              <form onSubmit={handlePayPortfolio}>
                <div className="form-group"><label>Fecha</label><input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} required /></div>
                <div className="form-group"><label>Monto</label><input type="number" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} required /></div>
                <div className="modal-buttons">
                  <button type="submit" className="btn btn-primary">Confirmar</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowPaymentModal(false)}>Cancelar</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showAbonoWithdrawalModal && (
          <div className="modal-overlay" onClick={() => setShowAbonoWithdrawalModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Retirar Dinero de Abonos</h2>
              <form onSubmit={handleAddAbonoWithdrawal}>
                <div className="form-group"><label>Fecha</label><input type="date" value={abonoWithdrawalForm.created_at} onChange={(e) => setAbonoWithdrawalForm({...abonoWithdrawalForm, created_at: e.target.value})} required /></div>
                <div className="form-group"><label>Descripción</label><input type="text" value={abonoWithdrawalForm.description} onChange={(e) => setAbonoWithdrawalForm({...abonoWithdrawalForm, description: e.target.value})} required /></div>
                <div className="form-group"><label>A quién se le pagó (Beneficiario)</label><input type="text" placeholder="Ej: Nombre de la persona o entidad" value={abonoWithdrawalForm.payment_source} onChange={(e) => setAbonoWithdrawalForm({...abonoWithdrawalForm, payment_source: e.target.value})} required /></div>
                <div className="form-group"><label>Monto</label><input type="number" max={balance?.balance_abonos} value={abonoWithdrawalForm.amount} onChange={(e) => setAbonoWithdrawalForm({...abonoWithdrawalForm, amount: e.target.value})} required /></div>
                <div className="modal-buttons">
                  <button type="submit" className="btn btn-warning">Retirar</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAbonoWithdrawalModal(false)}>Cancelar</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de Gasto */}
        {showExpenseModal && (
          <div className="modal-overlay" onClick={() => setShowExpenseModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2 style={{ color: '#ef4444' }}>Registrar Gasto</h2>
              <form onSubmit={handleSaveExpense}>
                <div className="form-group">
                  <label>Fecha</label>
                  <input
                    type="date"
                    value={expenseForm.created_at}
                    onChange={(e) => setExpenseForm({...expenseForm, created_at: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Descripción</label>
                  <input
                    type="text"
                    placeholder="Ej: Pago de servicios, papelería, etc."
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Monto</label>
                  <input
                    type="number"
                    placeholder="0"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Medio de Pago (Opcional)</label>
                  <input
                    type="text"
                    placeholder="Ej: Efectivo, Nequi"
                    value={expenseForm.payment_source}
                    onChange={(e) => setExpenseForm({...expenseForm, payment_source: e.target.value})}
                  />
                </div>
                <div className="modal-buttons">
                  <button type="submit" className="btn btn-danger" style={{ background: '#ef4444' }}>Guardar Gasto</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowExpenseModal(false)}>Cancelar</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CajaAbonos;
